import type { CSSProperties } from 'react'
import { createTheme } from '@mui/material/styles'

declare module '@mui/material/styles' {
  interface TypographyVariants {
    badge: CSSProperties
  }
  interface TypographyVariantsOptions {
    badge?: CSSProperties
  }
}

declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    badge: true
  }
}

// Professional blue and gray color palette for MissFit Mindset
//
// This palette is frozen. Every value below already shipped, and the design
// revamp (MWA-340) deliberately adds none: the work there is type, spacing,
// separation and state, never hue. A new colour here is a bug.
const colors = {
  // MissFit brand navy — the same value the client emails are built on
  // (BRAND.navy in backend/layers/helpers/nodejs/email-templates.mjs).
  // Note this is NOT the app chrome: MuiAppBar and MuiDrawer use gray900.
  brandNavy: '#002147',

  // Primary blues
  primary: '#1976d2',
  primaryLight: '#42a5f5',
  primaryDark: '#1565c0',

  // Secondary grays
  secondary: '#6b7280',
  secondaryLight: '#9ca3af',
  secondaryDark: '#4b5563',

  // Neutral grays
  gray50: '#f9fafb',
  gray100: '#f3f4f6',
  gray200: '#e5e7eb',
  gray300: '#d1d5db',
  gray400: '#9ca3af',
  gray500: '#6b7280',
  gray600: '#4b5563',
  gray700: '#374151',
  gray800: '#1f2937',
  gray900: '#111827',
}

// Status text on a light surface, using only palette values.
//
// KNOWN ACCEPTED GAP: against white, success.dark reads 3.8:1 and warning.dark
// reads 3.2:1, both under the 4.5:1 bar PRODUCT.md records as the team
// standard. error.dark (4.8:1) and primary.dark (6.0:1) pass. Francisco chose
// on 2026-09-17 to freeze the palette and accept this rather than add two
// darker text-only tokens. Do not "fix" it by inventing a hex value; that is a
// separate decision. Mitigated where it costs nothing: every status chip also
// carries a word, so colour is never the only thing telling you what a row is.

// The body sets this for the whole app (MuiCssBaseline below), so a new number
// is tabular without anyone remembering to ask. Spreading it is only needed
// where something resets font-variant-numeric.
export const tabularNums = { fontVariantNumeric: 'tabular-nums' as const }

// Scroll containers inside a page opt into themed scrollbars with this. It is
// deliberately NOT applied globally: the navigation drawer is out of scope for
// the revamp and keeps the scrollbar it has today.
export const scrollbarSx = {
  '&::-webkit-scrollbar': { width: 10, height: 10 },
  '&::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: colors.gray300,
    borderRadius: 99,
    border: '3px solid transparent',
    backgroundClip: 'content-box',
  },
  '&::-webkit-scrollbar-thumb:hover': { backgroundColor: colors.gray400 },
} as const

// Menus, dialogs and popovers are the only surfaces that cast a shadow. Cards
// separate with a border instead, so a real offset and blur here reads as
// "floating above the page" rather than as decoration.
const overlayShadow =
  '0 10px 28px -6px rgba(17, 24, 39, 0.18), 0 2px 6px -1px rgba(17, 24, 39, 0.08)'

export const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: colors.primary,
      light: colors.primaryLight,
      dark: colors.primaryDark,
      contrastText: '#ffffff',
    },
    secondary: {
      main: colors.secondary,
      light: colors.secondaryLight,
      dark: colors.secondaryDark,
      contrastText: '#ffffff',
    },
    success: {
      main: '#10b981',
      light: '#34d399',
      dark: '#059669',
    },
    warning: {
      main: '#f59e0b',
      light: '#fbbf24',
      dark: '#d97706',
    },
    error: {
      main: '#ef4444',
      light: '#f87171',
      dark: '#dc2626',
    },
    background: {
      default: colors.gray50,
      paper: '#ffffff',
    },
    text: {
      primary: colors.gray900,
      secondary: colors.gray600,
    },
    divider: colors.gray200,
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    // Before MWA-340 nothing below h6 was defined, so every table cell,
    // caption and label in the app was a MUI default or a one-off inline
    // override. These are those sizes.
    h1: {
      fontWeight: 700,
      color: colors.gray900,
    },
    h2: {
      fontWeight: 600,
      color: colors.gray900,
    },
    h3: {
      fontWeight: 600,
      color: colors.gray900,
    },
    // Page title.
    h4: {
      fontSize: '1.25rem',
      lineHeight: '1.625rem',
      fontWeight: 600,
      letterSpacing: '-0.015em',
      color: colors.gray900,
    },
    h5: {
      fontSize: '1.125rem',
      lineHeight: '1.5rem',
      fontWeight: 600,
      letterSpacing: '-0.01em',
      color: colors.gray800,
    },
    h6: {
      fontSize: '1rem',
      lineHeight: '1.375rem',
      fontWeight: 600,
      letterSpacing: '-0.01em',
      color: colors.gray800,
    },
    // Dialog and section title.
    subtitle1: {
      fontSize: '1rem',
      lineHeight: '1.375rem',
      fontWeight: 600,
      letterSpacing: '-0.01em',
    },
    // Section heading inside a panel or dialog.
    subtitle2: {
      fontSize: '0.8125rem',
      lineHeight: '1.125rem',
      fontWeight: 600,
    },
    // Body copy and form values.
    body1: {
      fontSize: '0.875rem',
      lineHeight: '1.25rem',
    },
    // Table cell — the app's most-read size.
    body2: {
      fontSize: '0.8125rem',
      lineHeight: '1.125rem',
    },
    // Field label and column header.
    caption: {
      fontSize: '0.75rem',
      lineHeight: '1rem',
      fontWeight: 600,
    },
    // Badge and count: tab counts, tier badges, scores. Not status chips,
    // which stay at the chip's own size. Reach it with `typography: 'badge'`
    // in sx, or `<Typography variant="badge">`.
    badge: {
      fontSize: '0.6875rem',
      lineHeight: '0.875rem',
      fontWeight: 600,
      ...tabularNums,
    },
    button: {
      fontSize: '0.8125rem',
      fontWeight: 600,
    },
  },
  // Do NOT change this value. Layout.tsx styles the navigation items with
  // `borderRadius: 2`, which MUI multiplies by this, so the shell's 16px
  // radius depends on it staying 8. Every radius the revamp introduces is set
  // explicitly on its own component instead.
  shape: {
    borderRadius: 8,
  },
  // State-change motion is 150-200ms. MUI's `short` (250) drives buttons and
  // `standard` (300) is the default for every other create() call, which is
  // chips, cards, tab indicators and collapses. The enter and exit durations
  // of dialogs, drawers and menus are left at MUI's values: they are not state
  // changes on a control, and the mobile navigation drawer is one of them.
  transitions: {
    duration: {
      short: 200,
      standard: 200,
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: tabularNums,
        // Browsers reset font-variant-numeric on form controls through the
        // `font` shorthand, so tab counts and button counts would lose it.
        'button, input, select, textarea': {
          fontVariantNumeric: 'inherit',
        },
        // The app shell is out of scope for the revamp: the AppBar and the
        // left-anchored navigation drawers keep their proportional figures.
        '.MuiAppBar-root, .MuiDrawer-paperAnchorLeft': {
          fontVariantNumeric: 'normal',
        },
        '::selection': {
          backgroundColor: 'rgba(25, 118, 210, 0.22)',
        },
        // Keyboard focus ring. The `html` prefix is load-bearing: ButtonBase,
        // Chip and Link reset `outline: 0` in single-class rules injected after
        // this sheet, so an unprefixed selector loses to them. `.Mui-focusVisible`
        // reaches controls whose focused element is a hidden input
        // (Checkbox, Radio, Switch), where the ButtonBase wrapper carries it.
        // A Link rendered as a button sets `outline: auto` on
        // `.MuiLink-button.Mui-focusVisible`, two classes, so it needs its own
        // two-class entry to get the ring instead of the browser default.
        'html :focus-visible, html .Mui-focusVisible, html .MuiLink-button.Mui-focusVisible': {
          outline: `2px solid ${colors.primary}`,
          outlineOffset: 2,
        },
        // Excluded, each for a reason:
        // - Text fields show focus with their own primary border, and browsers
        //   match :focus-visible on every click into one.
        // - Menu items and autocomplete options show focus with a background,
        //   and a Select opened by mouse focuses its selected option in a way
        //   Chrome treats as :focus-visible, so a ring there appears on click.
        // - Elements with tabindex -1 are focus containers MUI moves focus into
        //   (dialog, popover, menu list), not controls. ButtonBase items keep
        //   the ring because Tabs rove focus onto tabindex -1 tabs.
        'html :is(.MuiInputBase-input, input, textarea, select, [contenteditable]):focus-visible, html :is(.MuiMenuItem-root, .MuiAutocomplete-option):is(:focus-visible, .Mui-focusVisible), html [tabindex="-1"]:not(.MuiButtonBase-root):focus-visible':
          {
            outline: 'none',
          },
        // The page scrollbar. Inner containers opt in via scrollbarSx; the
        // navigation drawer is deliberately untouched.
        'body::-webkit-scrollbar': { width: 10 },
        'body::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
        'body::-webkit-scrollbar-thumb': {
          backgroundColor: colors.gray300,
          borderRadius: 99,
          border: '3px solid transparent',
          backgroundClip: 'content-box',
        },
        '@media (prefers-reduced-motion: reduce)': {
          '*, *::before, *::after': {
            animationDuration: '0.01ms !important',
            transitionDuration: '0.01ms !important',
          },
        },
      },
    },
    // The focus ring replaces MUI's pulsing focus ripple on keyboard focus,
    // here and on the other ripple controls below. Click ripples are untouched.
    MuiButton: {
      defaultProps: { disableFocusRipple: true },
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          borderRadius: 6,
        },
        contained: {
          boxShadow: 'none',
          '&:hover': {
            boxShadow: 'none',
          },
          // The focus ring is the keyboard indicator; MUI's elevation on
          // focus would stack a shadow under it.
          '&.Mui-focusVisible': {
            boxShadow: 'none',
          },
        },
      },
    },
    MuiIconButton: { defaultProps: { disableFocusRipple: true } },
    MuiFab: { defaultProps: { disableFocusRipple: true } },
    MuiCheckbox: { defaultProps: { disableFocusRipple: true } },
    MuiRadio: { defaultProps: { disableFocusRipple: true } },
    MuiSwitch: { defaultProps: { disableFocusRipple: true } },
    MuiCard: {
      styleOverrides: {
        root: {
          // Borders replace Material elevation everywhere except overlays.
          boxShadow: 'none',
          borderRadius: 10,
          border: `1px solid ${colors.gray200}`,
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: colors.gray900,
        },
      },
    },
    // Containers are 10px. Paper's rounded corner otherwise comes from
    // shape.borderRadius (8), which cannot change; this is what reaches table
    // containers, alerts and plain Paper panels. Components that set their own
    // radius (Dialog, Menu, Card, full-screen dialogs, Accordion) still win.
    MuiPaper: {
      styleOverrides: {
        rounded: {
          borderRadius: 10,
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        // The navigation drawer. Content drawers elsewhere must override
        // PaperProps themselves or they inherit this dark surface and render
        // unreadable — the cause of MWA-253's defects.
        //
        // A temporary drawer is an overlay and casts the overlay shadow in
        // place of MUI's elevation 16. Left-anchored drawers are the
        // navigation, which is app shell and keeps the shadow it has.
        paper: ({ ownerState }) => ({
          backgroundColor: colors.gray900,
          color: '#ffffff',
          ...(ownerState.variant === 'temporary' &&
            ownerState.anchor !== 'left' && { boxShadow: overlayShadow }),
        }),
      },
    },
    // A determinate bar otherwise eases to a new value over MUI's fixed 400ms.
    MuiLinearProgress: {
      styleOverrides: {
        bar: {
          transitionDuration: '200ms',
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 10,
          boxShadow: overlayShadow,
          backgroundImage: 'none',
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          borderRadius: 10,
          border: `1px solid ${colors.gray200}`,
          boxShadow: overlayShadow,
          backgroundImage: 'none',
        },
      },
    },
    MuiPopover: {
      styleOverrides: {
        paper: {
          borderRadius: 10,
          boxShadow: overlayShadow,
          backgroundImage: 'none',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 500,
          borderRadius: 6,
        },
        // Status chips: white ground, `.main` border, `.dark` text. The palette
        // has no light tints (its `.light` values are mid-tones), so a filled
        // tint is not available. `default` has no palette entry and takes the
        // strong border and secondary text instead. MUI's clickable hover and
        // focus backgrounds are more specific than this and still apply.
        outlined: ({ ownerState, theme }) => {
          const tone =
            ownerState.color && ownerState.color !== 'default'
              ? theme.palette[ownerState.color]
              : undefined
          return {
            backgroundColor: theme.palette.background.paper,
            borderColor: tone ? tone.main : colors.gray300,
            color: tone ? tone.dark : theme.palette.text.secondary,
          }
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          fontSize: '0.8125rem',
          lineHeight: '1.125rem',
          borderBottom: `1px solid ${colors.gray100}`,
        },
        head: {
          fontSize: '0.75rem',
          fontWeight: 600,
          lineHeight: '1rem',
          color: colors.gray600,
          backgroundColor: colors.gray50,
          borderBottom: `1px solid ${colors.gray200}`,
        },
      },
    },
    MuiTab: {
      defaultProps: { disableFocusRipple: true },
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontSize: '0.8125rem',
          fontWeight: 500,
          minHeight: 36,
          '&.Mui-selected': {
            fontWeight: 600,
          },
        },
      },
    },
    MuiToggleButton: {
      defaultProps: { disableFocusRipple: true },
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 6,
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: colors.primary,
          },
        },
        // MUI hardcodes 50ms and 100ms on the label notch; bring it into the
        // 150-200ms motion range. The notched state keeps its 50ms delay, so the
        // gap still finishes opening with the 200ms label shrink.
        notchedOutline: {
          '& > legend': { transitionDuration: '150ms' },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            '&:hover fieldset': {
              borderColor: colors.primary,
            },
          },
        },
      },
    },
  },
})

export { colors }
